/**
 * Строка «Итого» над таблицей накопления: что держит ГРУППА кошельков.
 *
 * # Зачем она есть
 *
 * Панель отвечает по одному кошельку, а вопрос оператора часто про нескольких
 * сразу: эти четверо, которых он считает одной рукой, — они вместе за Up или
 * против? По строкам такую сумму складывают в уме и складывают неверно: «+300
 * Up» и «+250 Down» это не «+550», а «+50 Up», и знак зависит от того, какой
 * исход считать первым.
 *
 * # Что складывается, а что нет
 *
 * Складывается всё, у чего сумма имеет тот же смысл, что слагаемое: шеры, доллары,
 * сделки, пнл. Не складываются ПРОИЗВОДНЫЕ — средняя цена и РОЙ. Они считаются
 * заново из сумм (Σденег ÷ Σшеров, Σпнл ÷ Σвложенного), потому что среднее
 * средних отвечает на вопрос, которого никто не задавал.
 *
 * # Третья величина: ПРЕДСКАЗАНИЕ
 *
 * Она не сумма и не производная от суммы одной стороны — это цена, по которой
 * группа торговала, приведённая к оси ОДНОГО исхода. Купить Down за 0.2 значит,
 * что напротив заплатили 0.8 за Up, поэтому обе покупки говорят про одну ось, а
 * весом служат шеры. Отвечает на вопрос, которого не закрывает ни перевес, ни
 * средняя цена: во что группа верит и дешевле ли этого стакан прямо сейчас.
 *
 * Расходиться с колонкой «Исход» она может открыто, и это не дефект — см.
 * `forecastOutcome`.
 *
 * # Главная ловушка и как она закрыта
 *
 * Отмеченный кошелёк может не торговать в ЭТОМ окне — отметки живут дольше окна
 * (см. store/useParticipantPicks). Сложить четверых из шести и подписать «итого
 * по шести» значило бы соврать суммой, поэтому строка несёт `missing`, а панель
 * обязана это показать.
 *
 * Вторая ловушка того же рода — правые колонки: у каждого кошелька своя глубина
 * наблюдения (у одного сутки, у другого неделя), и Σ пнл по ним — это сумма за
 * РАЗНЫЕ периоды. Поле `flowRows` считает, у скольких свод вообще есть, и
 * подпись обязана назвать это число.
 */
import type { ParticipantRow, ParticipantsStats, Token } from "../api/types";
import { div, fixed, mul, parse, roundToFixed, SCALE, type Micro } from "./money";
import {
  direction,
  formatPercent,
  signedDollars,
  signedPrice,
} from "./participants";
import { formatDollars, formatShares } from "./tape";

/**
 * С какой уверенности предсказание считается предсказанием.
 *
 * Ниже порога группа не «слабо уверена», а НЕ ОПРЕДЕЛИЛАСЬ: цена около половины
 * означает, что деньги стояли по обе стороны примерно поровну, и отклонение от
 * такой середины — это разница двух шумов. Порог назначен оператором, а не
 * выведен из данных, и держится здесь ровно поэтому: одно место, где его менять.
 */
export const FORECAST_DECIDED = 650_000;

/** Ровно половина. Ей отделяется «сторона есть» от «стороны нет». */
const HALF = SCALE / 2;

/** Ширина корзины распределения: 0.05, то есть двадцать корзин на всю ось. */
const BUCKET = SCALE / 20;

/** Одна корзина распределения денег по цене входа. */
export interface ForecastBucket {
  /** Нижняя граница, «0.75». */
  from: string;
  /** Верхняя, «0.80». */
  to: string;
  /** Σ денег покупок, попавших сюда. */
  usd: string;
  /** Σ шеров — ИМИ корзина весит в предсказании, поэтому полоска рисуется по ним. */
  shares: string;
  /** Доля корзины по шерам относительно самой крупной, 0..1. Длина полоски. */
  weight: number;
}

/** Во что группа зашла по одному исходу — знаменатель честности у «ставки». */
export interface ForecastStake {
  outcome: string;
  usd: string;
  shares: string;
  /** Средняя цена покупок ЭТОГО исхода, до приведения к общей оси. */
  price: string;
}

export interface TotalsLine {
  /** Строк, сложенных в итог. */
  rows: number;
  /** Отмечено галочками ВСЕГО, включая кошельки не из этого окна. */
  picked: number;
  /** Из отмеченных — сколько в этом окне не торговало. НЕ ноль, а «их нет». */
  missing: number;
  /** Итог по отмеченным. Ложь — по всем показанным строкам. */
  byPicks: boolean;

  /** Исход, по которому группа В ЛОНГЕ. Пусто — перевеса нет либо рынок не бинарный. */
  outcome: string;
  /** Перевес в share, ВСЕГДА неотрицательный: знак несёт outcome. */
  net: string;
  /**
   * Во что встал ПОКАЗАННЫЙ исход: куплено минус продано, в долларах.
   *
   * Именно по показанному токену, а не по обоим: сложить деньги двух исходов
   * значило бы назвать ценой направления сумму, потраченную против него же.
   */
  netUsd: string;
  taker: string | null;
  maker: string | null;
  /** Средневзвешенная цена покупок показанного исхода. */
  avg: string;
  /** Вес этой средней: сколько шеров куплено. Без него средняя читается как цена позиции. */
  bought: string;
  trades: number;

  /**
   * ПРЕДСКАЗАНИЕ группы: по какой цене она считает исход вероятным.
   *
   * Сведённая цена сделок, приведённая к оси ОДНОГО исхода: купить Down за 0.200
   * значит, что напротив заплатили 0.800 за Up, и обе покупки говорят про одну и
   * ту же ось. Всегда ≥ 0.5 — сторона вынесена в `forecastOutcome`.
   *
   * Пусто: рынок не бинарный либо покупок не было вовсе.
   */
  forecast: string;
  /** То же числом — вход раскраски и сравнения со стаканом. */
  forecastMicro: Micro | null;
  /**
   * Исход, на который указывает предсказание.
   *
   * НЕ ТО ЖЕ, что `outcome`, и расходиться с ним они могут открыто: `outcome` —
   * куда группа в лонге по НЕТТО, а это — по какой цене она входила. Группа,
   * купившая только Down по 0.2, стоит в лонге Down и предсказывает Up 0.800.
   *
   * Пусто при ровной половине: стороны нет, и называть её нечем.
   */
  forecastOutcome: string;
  /** `asset_id` предсказанного исхода — по нему разметка берёт аск из стакана. */
  forecastAsset: string;

  /** Σ денег покупок, на которых стоит предсказание. По ОБОИМ исходам. */
  stake: string;
  /** Та же ставка по исходам — знаменатель честности для подсказки. */
  stakeByOutcome: ForecastStake[];
  /** Распределение денег по цене входа на оси предсказания. */
  buckets: ForecastBucket[];

  /** Σ итога окна по тем строкам, где он известен. */
  settled: string | null;
  /** У скольких строк итог есть. Меньше rows — сумма неполная, и это надо назвать. */
  settledRows: number;

  /** Σ взятого у нас, в share. Только в режиме «против нас». */
  took: string | null;

  /** Правые колонки: свод кошельков на коротких рынках. */
  bets: number | null;
  avgBet: string | null;
  pnl: string | null;
  roi: string | null;
  paid: string | null;
  /** У скольких строк свод есть. Знаменатель всех четырёх чисел выше. */
  flowRows: number;

  /** Адреса сложенных строк — вход групповой правки пометок. */
  wallets: string[];
  /**
   * Пометки, стоящие на ВСЕХ сложенных кошельках сразу.
   *
   * Именно на всех, а не на любом: это ответ на вопрос «что стоит на группе», и
   * от него зависит, что делает нажатие в окошке правки. Тег, который есть у
   * половины, группе не принадлежит — нажатие на него ДОБИРАЕТ остальных, и это
   * ровно то, чего оператор ждёт, выделив строки и ткнув в пометку.
   */
  tagsAll: string[];
}

/**
 * Сложить показанные строки — все либо только отмеченные.
 *
 * Считается ПО СЫРЫМ строкам ответа, а не по готовым `ParticipantLine`: там
 * нетто уже взято по модулю (знак вынесен в подпись исхода), и сумма модулей
 * дала бы «перевес» у группы, которая стоит по разные стороны рынка.
 */
export function totalsLine(
  p: ParticipantsStats | undefined,
  tokens: readonly Token[] | undefined,
  picked: ReadonlySet<string>,
): TotalsLine | null {
  if (!p?.known || p.rows.length === 0) return null;

  const byPicks = picked.size > 0;
  const rows = byPicks
    ? p.rows.filter((r) => picked.has(r.wallet.toLowerCase()))
    : p.rows;
  if (rows.length === 0) {
    // Отмечены кошельки, которых в этом окне нет вовсе. Строка всё равно есть, и
    // это не пустая формальность: без неё панель молча выглядела бы как «фильтр
    // ничего не нашёл», тогда как ответ — «эти шестеро здесь не торговали».
    return empty(picked.size);
  }

  const dir = sumDirection(rows, tokens);
  const flip = dir.net < 0;
  const sign = flip ? -1 : 1;
  // Нулевой перевес — направления НЕТ, и токена, о котором говорят деньги и
  // средняя цена, тоже нет. Пустой идентификатор гасит их разом — то же правило,
  // что в строке кошелька: назвать цену, не назвав, ЧЕГО она, значит предложить
  // прочитать её как цену рынка. Роль при этом остаётся видимой, и в ней весь
  // смысл такой строки: группа мейкеров, закрывшая окно в ноль, прогнала через
  // себя оборот.
  const assetID = dir.net === 0 ? "" : flip ? dir.shortAsset : dir.longAsset;

  const taker = p.truncated ? null : sign * dir.taker;
  const maker = p.truncated ? null : sign * dir.maker;

  const money = moneyOf(rows, assetID);
  const flow = flowOf(rows);
  const settled = settledOf(rows);
  // Предсказание считается по ОБОИМ исходам, а не по показанному: цена сделки
  // говорит про оба сразу, и выбрасывать половину значило бы спрашивать про
  // рынок, а слушать одну его сторону.
  const forecast = forecastOf(rows, tokens);

  return {
    ...forecast,
    rows: rows.length,
    picked: picked.size,
    missing: byPicks ? picked.size - rows.length : 0,
    byPicks,
    outcome: outcomeOf(tokens, assetID),
    net: formatShares(fixed(Math.abs(dir.net))),
    netUsd: money.net === null ? "" : signedDollars(fixed(money.net)) ?? "",
    taker: taker === null ? null : formatShares(fixed(noNegZero(taker))),
    maker: maker === null ? null : formatShares(fixed(noNegZero(maker))),
    avg: money.avg === null ? "" : roundToFixed(money.avg, 3),
    bought: money.bought === null ? "" : formatShares(fixed(money.bought)),
    trades: rows.reduce(
      (sum, r) => sum + r.tokens.reduce((n, t) => n + t.trades, 0),
      0,
    ),
    settled: settled.usd,
    settledRows: settled.rows,
    took: tookOf(rows),
    ...flow,
    wallets: rows.map((r) => r.wallet),
    tagsAll: commonTags(rows),
  };
}

/** Итог по отмеченным, которых в этом окне нет ни одного. */
function empty(picked: number): TotalsLine {
  return {
    ...noForecast(),
    rows: 0,
    picked,
    missing: picked,
    byPicks: true,
    outcome: "",
    net: formatShares("0"),
    netUsd: "",
    taker: null,
    maker: null,
    avg: "",
    bought: "",
    trades: 0,
    settled: null,
    settledRows: 0,
    took: null,
    bets: null,
    avgBet: null,
    pnl: null,
    roi: null,
    paid: null,
    flowRows: 0,
    wallets: [],
    tagsAll: [],
  };
}

/**
 * Пометки, общие для ВСЕХ сложенных строк.
 *
 * Пересечение, а не объединение: окошко правки показывает ими то, что стоит на
 * группе, и объединение обещало бы снятие тега у кошельков, у которых его нет.
 */
function commonTags(rows: readonly ParticipantRow[]): string[] {
  const first = rows[0];
  if (!first) return [];
  let common = [...(first.tags ?? [])];
  for (const row of rows.slice(1)) {
    const tags = row.tags ?? [];
    common = common.filter((t) => tags.includes(t));
    if (common.length === 0) break;
  }
  return common;
}

/**
 * Сведённое направление группы.
 *
 * Тем же `direction`, которым считается строка кошелька, и это главное свойство
 * функции: перевес группы обязан быть суммой перевесов её строк, иначе итог
 * перестанет сходиться с тем, что под ним написано.
 */
function sumDirection(
  rows: readonly ParticipantRow[],
  tokens: readonly Token[] | undefined,
): { net: number; taker: number; maker: number; longAsset: string; shortAsset: string } {
  let net = 0;
  let taker = 0;
  let maker = 0;
  let longAsset = "";
  let shortAsset = "";
  for (const row of rows) {
    const d = direction(row.tokens, tokens);
    net += d.net;
    taker += d.taker;
    maker += d.maker;
    longAsset = d.longAsset;
    shortAsset = d.shortAsset;
  }
  return { net, taker, maker, longAsset, shortAsset };
}

/**
 * Деньги показанного исхода: во что он встал и по какой средней.
 *
 * Средняя считается ИЗ СУММ, а не усреднением средних: кошелёк, купивший десять
 * шеров по 0.9, и кошелёк, купивший тысячу по 0.4, дают среднюю 0.405, а не 0.65.
 */
function moneyOf(
  rows: readonly ParticipantRow[],
  assetID: string,
): { avg: Micro | null; bought: Micro | null; net: Micro | null } {
  if (assetID === "") return { avg: null, bought: null, net: null };
  let bought = 0;
  let boughtUSD = 0;
  let soldUSD = 0;
  let seen = false;
  for (const row of rows) {
    const t = row.tokens.find((x) => x.asset_id === assetID);
    if (!t) continue;
    seen = true;
    bought += parse(t.bought) ?? 0;
    boughtUSD += parse(t.bought_usd) ?? 0;
    soldUSD += parse(t.sold_usd) ?? 0;
  }
  if (!seen) return { avg: null, bought: null, net: null };
  return {
    // Точным делением, а не `Number(a)/Number(b)`: правило то же, что у средней
    // цены одной строки (avgBuyPrice) — Σденег ÷ Σшеров, — и считается оно тем же
    // `div`, чтобы округление не разъехалось между строкой и итогом.
    avg: bought > 0 ? div(boughtUSD, bought) : null,
    bought,
    net: boughtUSD - soldUSD,
  };
}

/** Поля предсказания, когда его нет. Пусто, а не нули: ноль назвал бы цену. */
function noForecast(): Pick<
  TotalsLine,
  | "forecast"
  | "forecastMicro"
  | "forecastOutcome"
  | "forecastAsset"
  | "stake"
  | "stakeByOutcome"
  | "buckets"
> {
  return {
    forecast: "",
    forecastMicro: null,
    forecastOutcome: "",
    forecastAsset: "",
    stake: "",
    stakeByOutcome: [],
    buckets: [],
  };
}

/**
 * Предсказание группы: сведённая цена её входов на оси одного исхода.
 *
 * # Почему обе стороны складываются, а не вычитаются
 *
 * Купить Down за 0.200 и купить Up за 0.800 — это две записи об ОДНОЙ цене
 * рынка. Поэтому цена каждой покупки приводится к оси первого исхода
 * (`p` либо `1 − p`) и взвешивается шерами, как договорено с оператором.
 *
 * # Почему без промежуточных делений
 *
 * Потому что `шеры · цена = деньги`, и взвешенная сумма раскрывается точно:
 *
 *     числитель = Σ денег(первый) + Σ( шеры(второй) − деньги(второй) )
 *
 * Второе слагаемое — ровно те деньги, что в сделках второго исхода стояли на
 * стороне первого. Одно деление на всю группу вместо деления на каждый кошелёк:
 * то же правило, по которому средняя цена считается ИЗ СУММ (см. `moneyOf`), и
 * здесь оно ещё и убирает накопление ошибки округления по сотне строк.
 *
 * # Что НЕ входит
 *
 * Продажи. Предсказание — это цена ВХОДА, и та же граница стоит у `avg`: выход
 * отвечает на другой вопрос, а смешать их значило бы получить цену, по которой
 * не входил никто.
 */
function forecastOf(
  rows: readonly ParticipantRow[],
  tokens: readonly Token[] | undefined,
): Pick<
  TotalsLine,
  | "forecast"
  | "forecastMicro"
  | "forecastOutcome"
  | "forecastAsset"
  | "stake"
  | "stakeByOutcome"
  | "buckets"
> {
  const first = tokens?.[0];
  const second = tokens?.[1];
  // Небинарный рынок сводить не к чему — тот же отказ, что у `direction`:
  // приведение к одной оси есть утверждение «исходов два», и на трёхисходном
  // neg-risk оно было бы выдумкой.
  if (tokens?.length !== 2 || !first?.asset_id || !second?.asset_id) {
    return noForecast();
  }

  let sharesFirst = 0;
  let usdFirst = 0;
  let sharesSecond = 0;
  let usdSecond = 0;
  for (const row of rows) {
    for (const t of row.tokens) {
      const shares = parse(t.bought) ?? 0;
      const usd = parse(t.bought_usd) ?? 0;
      if (shares <= 0) continue;
      if (t.asset_id === first.asset_id) {
        sharesFirst += shares;
        usdFirst += usd;
      } else if (t.asset_id === second.asset_id) {
        sharesSecond += shares;
        usdSecond += usd;
      }
    }
  }

  const denom = sharesFirst + sharesSecond;
  if (denom <= 0) return noForecast();
  const pFirst = div(usdFirst + (sharesSecond - usdSecond), denom);
  // Вне [0, 1] — это не «уверенность больше единицы», а битые суммы с провода
  // (денег за исход больше, чем шеров). Показывать такое как вероятность нельзя.
  if (pFirst === null || pFirst < 0 || pFirst > SCALE) return noForecast();

  const up = pFirst > HALF;
  // Ровная половина: стороны НЕТ. Число остаётся — «группа торговала ровно
  // посередине» это ответ, — а исход не называется, и по нему же гаснет
  // отклонение: сравнивать со стаканом нечего, пока не сказано, чего стакан.
  const tie = pFirst === HALF;
  const side = up ? first : second;
  const forecastMicro = tie ? HALF : up ? pFirst : SCALE - pFirst;

  return {
    forecast: roundToFixed(forecastMicro, 3),
    forecastMicro,
    forecastOutcome: tie ? "" : side.outcome,
    forecastAsset: tie ? "" : side.asset_id,
    stake: formatDollars(fixed(usdFirst + usdSecond)),
    stakeByOutcome: [
      stakeOf(first, sharesFirst, usdFirst),
      stakeOf(second, sharesSecond, usdSecond),
    ].filter((s): s is ForecastStake => s !== null),
    // Корзины строятся на оси ПРЕДСКАЗАНИЯ, а при ровной половине — на оси
    // первого исхода: ось нужна распределению всегда, а имя стороны — нет.
    buckets: bucketsOf(rows, tie ? first.asset_id : side.asset_id),
  };
}

/** Во что группа зашла по одному исходу. null — не заходила вовсе. */
function stakeOf(
  token: Token,
  shares: Micro,
  usd: Micro,
): ForecastStake | null {
  if (shares <= 0) return null;
  const price = div(usd, shares);
  return {
    outcome: token.outcome,
    usd: formatDollars(fixed(usd)),
    shares: formatShares(fixed(shares)),
    price: price === null ? "" : roundToFixed(price, 3),
  };
}

/**
 * Распределение денег по цене входа на оси предсказания.
 *
 * Отвечает на вопрос, которого одно число не закрывает: согласны ли кошельки
 * между собой. Одна цена 0.795 бывает и у группы, где все вошли около 0.8, и у
 * группы, где половина взяла по 0.6, а половина по 0.95, — а это разные рынки.
 *
 * Считается по ПАРАМ (кошелёк, исход), а не по кошелькам: кошелёк, стоящий по
 * обе стороны, вошёл по двум разным ценам, и усреднить их в одну значило бы
 * поставить его в корзину, в которой он не покупал.
 */
function bucketsOf(
  rows: readonly ParticipantRow[],
  axisAsset: string,
): ForecastBucket[] {
  const usd = new Array<number>(20).fill(0);
  const shares = new Array<number>(20).fill(0);
  for (const row of rows) {
    for (const t of row.tokens) {
      const bought = parse(t.bought) ?? 0;
      if (bought <= 0) continue;
      const boughtUSD = parse(t.bought_usd) ?? 0;
      const price = div(boughtUSD, bought);
      if (price === null) continue;
      const onAxis = t.asset_id === axisAsset ? price : SCALE - price;
      // Край 1.0 попадает в последнюю корзину, а не в двадцать первую.
      const i = Math.min(19, Math.max(0, Math.floor(onAxis / BUCKET)));
      usd[i] = (usd[i] ?? 0) + boughtUSD;
      shares[i] = (shares[i] ?? 0) + bought;
    }
  }

  const top = Math.max(...shares);
  if (top <= 0) return [];
  const out: ForecastBucket[] = [];
  for (let i = 0; i < 20; i++) {
    const s = shares[i] ?? 0;
    if (s <= 0) continue;
    out.push({
      from: roundToFixed(i * BUCKET, 2),
      to: roundToFixed((i + 1) * BUCKET, 2),
      usd: formatDollars(fixed(usd[i] ?? 0)),
      shares: formatShares(fixed(s)),
      weight: s / top,
    });
  }
  return out;
}

/**
 * Отклонение стакана от предсказания и можно ли его красить.
 *
 * # Почему аск, а не середина
 *
 * Потому что «зайти» значит купить, а покупают по аску. Середина назвала бы
 * форой половину спреда, которой на входе не существует.
 *
 * # Почему знак такой
 *
 * Плюс — стакан ДЕШЕВЛЕ, чем в среднем входила группа, то есть мы берём тот же
 * исход с форой. Минус — дороже.
 *
 * # Почему `decided` отдельно от знака
 *
 * Ниже порога сторона не выбрана (см. FORECAST_DECIDED), и раскрашивать
 * отклонение от несуществующего мнения — это дать зелёный свет шуму. Число при
 * этом остаётся на экране: скрыть его значило бы прятать причину, по которой
 * сигнала нет.
 */
export function forecastEdge(
  t: TotalsLine | null,
  ask: string | null,
): { label: string | null; decided: boolean } {
  const p = t?.forecastMicro ?? null;
  const decided = p !== null && p > FORECAST_DECIDED;
  if (!t || p === null || t.forecastAsset === "") {
    return { label: null, decided: false };
  }
  const a = parse(ask);
  if (a === null) return { label: null, decided };
  return { label: signedPrice(fixed(p - a), 3), decided };
}

/** Σ итога окна и сколько строк его вообще имеют. */
function settledOf(rows: readonly ParticipantRow[]): {
  usd: string | null;
  rows: number;
} {
  let sum = 0;
  let n = 0;
  for (const row of rows) {
    const v = row.settled ? parse(row.settled.pnl_usd) : null;
    if (v === null) continue;
    sum += v;
    n++;
  }
  // Ноль строк — прочерк, а не «$0»: ноль сказал бы «группа вышла в ноль», а мы
  // говорим «итога не знаем». Та же грамматика, что в строке кошелька.
  return { usd: n === 0 ? null : signedDollars(fixed(sum)), rows: n };
}

/** Σ взятого у нас в share. Только в режиме «против нас». */
function tookOf(rows: readonly ParticipantRow[]): string | null {
  let sum = 0;
  let n = 0;
  for (const row of rows) {
    const v = row.against_us ? parse(row.against_us.took) : null;
    if (v === null) continue;
    sum += v;
    n++;
  }
  return n === 0 ? null : formatShares(fixed(sum));
}

/**
 * Правые колонки: свод группы на коротких рынках.
 *
 * Оговорка, которую обязана повторить подпись: глубина у кошельков РАЗНАЯ, и это
 * сумма за разные периоды. Она всё равно полезнее прочерка — вопрос «сколько эта
 * группа зарабатывает вообще» задают именно так, — но названа должна быть честно.
 */
function flowOf(rows: readonly ParticipantRow[]): Pick<
  TotalsLine,
  "bets" | "avgBet" | "pnl" | "roi" | "paid" | "flowRows"
> {
  let bets = 0;
  let markets = 0;
  let boughtUSD = 0;
  let soldUSD = 0;
  let pnl = 0;
  let seen = 0;
  for (const row of rows) {
    if (!row.flow) continue;
    seen++;
    bets += row.flow.trades;
    markets += row.flow.markets;
    boughtUSD += parse(row.flow.bought_usd) ?? 0;
    soldUSD += parse(row.flow.sold_usd) ?? 0;
    pnl += parse(row.flow.pnl_usd) ?? 0;
  }

  let paid: number | null = null;
  for (const row of rows) {
    const v = row.incentives ? parse(row.incentives.usd) : null;
    if (v === null) continue;
    paid = (paid ?? 0) + v;
  }

  if (seen === 0) {
    return {
      bets: null,
      avgBet: null,
      pnl: null,
      roi: null,
      paid: paid === null ? null : signedDollars(fixed(paid)),
      flowRows: 0,
    };
  }
  // Число окон — обычное целое, и в микро-единицы его надо ПОДНЯТЬ: делить микро
  // на целое значило бы завысить результат в миллион раз. Та же оговорка стоит у
  // averageBet в строке кошелька.
  const avgBet = markets > 0 ? div(boughtUSD + soldUSD, markets * SCALE) : null;
  const ratio = boughtUSD > 0 ? div(pnl, boughtUSD) : null;
  return {
    bets,
    // Оборот на окно: Σ оборота ÷ Σ окон. Не на сделки — дробление входа на сотню
    // мелких филлов это техника исполнения, а не размер игры (тот же довод, что в
    // строке кошелька).
    avgBet: avgBet === null ? null : formatDollars(fixed(avgBet)),
    pnl: signedDollars(fixed(pnl)),
    // РОЙ группы — Σпнл ÷ Σвложенного, а не среднее РОЕВ: у кошелька с оборотом в
    // сто долларов и у кошелька с оборотом в сто тысяч они весят одинаково только
    // в среднем арифметическом, и это ровно то, чего никто не спрашивает.
    roi: ratio === null ? null : formatPercent(mul(ratio, 100 * SCALE)),
    paid: paid === null ? null : signedDollars(fixed(paid)),
    flowRows: seen,
  };
}

function outcomeOf(
  tokens: readonly Token[] | undefined,
  assetID: string,
): string {
  return tokens?.find((t) => t.asset_id === assetID)?.outcome ?? "";
}

/** −0 это 0: минус перед нулём читается как направление, которого нет. */
function noNegZero(v: number): number {
  return v === 0 ? 0 : v;
}

/**
 * Подпись у строки «Итого»: по кому она посчитана.
 *
 * # Почему только про отмеченных
 *
 * Потому что это ТЕКСТ ЧЕСТНОСТИ, а честность здесь ровно одна: сумма по
 * четверым из шести отмеченных читается как сумма по шестерым. Итог БЕЗ отметок
 * считается по всем показанным строкам — это умолчание панели, и называть его
 * словами значит занять строку под пересказ того, что и так на экране.
 *
 * Полнота знания (у скольких строк известен итог окна, у скольких есть свод на
 * коротких рынках) уехала в ПОДСКАЗКИ тех самых колонок, к которым относится, —
 * они там и были всё это время (см. flowTotalsTitle и подсказку колонки «Итог»).
 * Здесь эти оговорки повторяли сказанное и стоили двух строк высоты в шапке
 * таблицы, а высота шапки оплачивается не прокруткой, а строками кошельков.
 */
export function totalsNote(t: TotalsLine | null): string {
  if (!t || !t.byPicks) return "";
  if (t.rows === 0) {
    return `отмечено ${t.picked}, и ни один из них в этом окне не торговал`;
  }
  // Знаменатель — только когда он отличается: «по 6 отмеченным из 6» это то же
  // самое число дважды.
  return t.missing > 0
    ? `по ${t.rows} отмеченным из ${t.picked}`
    : `по ${t.rows} отмеченным`;
}

/**
 * Та же подпись целиком — подсказкой на строке «Итого».
 *
 * Ничего не заменяет и никуда не прячет: короткая строка отвечает «по кому
 * сумма», а полная — «чего в ней не хватает». Второе спрашивают, уже усомнившись
 * в числе, и место такому ответу в подсказке, а не в постоянно занятой строке.
 */
export function totalsTitle(t: TotalsLine | null): string {
  if (!t) return "";
  const parts: string[] = [];
  parts.push(
    t.byPicks
      ? t.rows === 0
        ? `отмечено ${t.picked}, и ни один из них в этом окне не торговал`
        : `сложено по ${t.rows} отмеченным кошелькам из ${t.picked}`
      : `сложено по всем ${t.rows} показанным строкам`,
  );
  if (t.byPicks && t.missing > 0 && t.rows > 0) {
    parts.push(`${t.missing} в этом окне не торговали и в сумму не вошли`);
  }
  if (t.rows > 0 && t.settledRows < t.rows) {
    parts.push(
      t.settledRows === 0
        ? "итог окна неизвестен ни у кого"
        : `итог окна известен у ${t.settledRows} из ${t.rows}`,
    );
  }
  if (t.rows > 0 && t.flowRows < t.rows) {
    parts.push(
      t.flowRows === 0
        ? "своды на коротких рынках не читались"
        : `свод на коротких рынках есть у ${t.flowRows} из ${t.rows}`,
    );
  }
  return parts.join("; ");
}

/**
 * Подсказка предсказания: откуда взялось число и согласны ли кошельки.
 *
 * Главное здесь — не формула, а ОГОВОРКА про расхождение с колонкой «Исход».
 * Группа, купившая только Down, стоит в лонге Down и предсказывает Up, и без
 * этих слов строка читается как противоречие самой себе.
 */
export function forecastTitle(t: TotalsLine | null): string {
  if (!t || t.forecast === "") {
    return "предсказания нет: рынок не бинарный либо покупок в нём не было";
  }
  const parts: string[] = [
    "Сведённая цена входов группы, приведённая к оси одного исхода: " +
      "Σ(шеры · цена) ÷ Σшеры по покупкам ОБОИХ исходов. Купить Down за 0.200 " +
      "значит, что напротив заплатили 0.800 за Up — обе покупки говорят про одну ось.",
    "Это НЕ перевес позиции. Колонка «Исход» говорит, куда группа в лонге по нетто, " +
      "а предсказание — по какой цене она входила, и указывать они могут в разные стороны.",
  ];
  if (t.forecastOutcome === "") {
    parts.push(
      "Ровная половина: стороны нет, и назвать её нечем — отклонение от стакана поэтому не считается.",
    );
  }
  if (t.buckets.length > 0) {
    parts.push(
      "Распределение денег по цене входа:\n" +
        t.buckets
          .map(
            (b) =>
              `${b.from}–${b.to}  ${b.usd}  ${"█".repeat(
                Math.max(1, Math.round(b.weight * 8)),
              )}`,
          )
          .join("\n"),
    );
  }
  return parts.join("\n\n");
}

/** Подсказка ставки: по кому и по каким исходам она сложена. */
export function stakeTitle(t: TotalsLine | null): string {
  if (!t || t.stake === "") return "";
  const head =
    "Σ денег покупок, на которых стоит предсказание — по ОБОИМ исходам, " +
    "потому что цена сделки говорит про оба сразу. Продажи не входят: " +
    "предсказание это цена ВХОДА.";
  if (t.stakeByOutcome.length === 0) return head;
  return `${head}\n\n${t.stakeByOutcome
    .map((s) => `${s.outcome}  ${s.usd} · ${s.shares} шеров по ${s.price}`)
    .join("\n")}`;
}

/**
 * Подсказка отклонения: что сравнивается, с чем и насколько этому верить.
 *
 * Состояние стакана называется ВСЕГДА, когда оно не «live»: отклонение считается
 * от вершины книги, и книга, у которой не сомкнута цепочка обновлений, даёт
 * вершину, которой в рынке может уже не быть.
 */
export function edgeTitle(
  t: TotalsLine | null,
  ask: string | null,
  status: string,
): string {
  if (!t || t.forecast === "") return "предсказания нет — сравнивать нечего";
  if (t.forecastAsset === "") {
    return "сторона не названа (ровно 0.500), и отклонение от стакана без неё бессмысленно";
  }
  const parts: string[] = [];
  if (ask === null) {
    parts.push(
      `лучшего аска по исходу ${t.forecastOutcome} нет — ${bookNote(status)}`,
    );
    return parts.join("\n\n");
  }
  parts.push(
    `Предсказание ${t.forecast} ${t.forecastOutcome} против лучшего аска ${ask}. ` +
      "Плюс — стакан ДЕШЕВЛЕ, чем в среднем входила группа; минус — дороже.",
  );
  parts.push(
    "Аск, а не середина: зайти значит купить, а покупают по аску — середина " +
      "назвала бы форой половину спреда, которой на входе нет.",
  );
  if (t.forecastMicro !== null && t.forecastMicro <= FORECAST_DECIDED) {
    parts.push(
      `Предсказание не перевалило ${roundToFixed(FORECAST_DECIDED, 2)} — исход ` +
        "считается неопределившимся, и отклонение не красится: это разница двух шумов.",
    );
  }
  if (status !== "live") parts.push(`Стакан: ${bookNote(status)}.`);
  return parts.join("\n\n");
}

/** Состояние стакана словами. Незнакомое состояние называется как есть. */
function bookNote(status: string): string {
  if (status === "live") return "стакан сомкнут и свеж";
  if (status === "waiting") return "опорного снимка ещё нет";
  if (status === "syncing") return "цепочка обновлений не сомкнута";
  if (status === "absent") return "трейдер молчит либо стакан у него не подтверждён";
  if (status === "error") return "стакан не читается";
  return status;
}
